import { apiFetch, get, post } from "./api";

export type EntityKind = "Quote" | "Job" | "Invoice" | "Account" | "Client";

export interface ThreadAttachment { fileName: string; downloadUrl: string | null; }

export interface ThreadMessage {
  source: "EmailLog" | "EmailMessage";
  sourceId: number;
  direction: "Outbound" | "Inbound";
  subject: string;
  from: string | null;
  to: string | null;
  cc: string | null;
  timestampUtc: string;
  bodyHtml: string | null;
  snippet: string | null;
  status: string | null;
  matchConfidence: number | null;
  attachments: ThreadAttachment[];
}

export interface SentEmailItem {
  emailLogId: number;
  kind: string;
  subject: string;
  toEmailAddress: string;
  relatedEntityType: string | null;
  relatedEntityId: number | null;
  sentAtUtc: string;
  sentSuccessfully: boolean;
  status: string | null;
}

export interface ComposeAttachmentRef { blobKey: string; fileName: string; contentType: string | null; }

export interface InboxListItem {
  emailMessageId: number;
  from: string;
  subject: string;
  snippet: string | null;
  receivedAtUtc: string;
  isRead: boolean;
  hasAttachments: boolean;
  matchedEntityType: string | null;
  matchedEntityId: number | null;
  matchConfidence: number;
  junkFlag: boolean;
  isBounce: boolean;
}
export interface InboxPage { items: InboxListItem[]; total: number; }
export interface FromAddress { code: string; description: string; emailAddress: string | null; }

/** Raw `EmailMessage` entity as returned by GET /api/email/messages/{id} (camelCased). */
export interface InboxMessageDetail {
  emailMessageId: number;
  direction: string;
  fromEmailAddress: string;
  toEmailAddress: string | null;
  cc: string | null;
  bcc: string | null;
  subject: string;
  bodyHtml: string | null;
  bodySnippet: string | null;
  receivedAtUtc: string;
  matchConfidence: number;
  junkFlag: boolean;
  isRead: boolean;
  isBounce: boolean;
  attachmentBlobKeys: string | null;
  jobId: number | null;
  quoteId: number | null;
  invoiceId: number | null;
  accountId: number | null;
}

export interface ComposeSendRequest {
  entityType: EntityKind;
  entityId: number;
  to: string;
  cc?: string | null;
  bcc?: string | null;
  subject: string;
  bodyHtml: string;
  attachments?: ComposeAttachmentRef[];
  fromCode?: string | null;
}

const pluralOf: Record<EntityKind, string> = {
  Quote: "quotes", Job: "jobs", Invoice: "invoices", Account: "accounts", Client: "clients",
};

export const emailApi = {
  thread: (entityType: EntityKind, entityId: number) =>
    get<ThreadMessage[]>(`/api/${pluralOf[entityType]}/${entityId}/email-thread`),

  sent: (params: { q?: string; status?: string; page?: number; pageSize?: number }) => {
    const qs = new URLSearchParams();
    if (params.q) qs.set("q", params.q);
    if (params.status) qs.set("status", params.status);
    qs.set("page", String(params.page ?? 0));
    qs.set("pageSize", String(params.pageSize ?? 50));
    return get<{ items: SentEmailItem[]; total: number }>(`/api/email/sent?${qs.toString()}`);
  },

  send: (body: ComposeSendRequest) =>
    post<{ emailLogId: number; sentSuccessfully: boolean; errorMessage: string | null }>(
      "/api/email/send", body),

  uploadAttachment: async (file: File): Promise<{ blobKey: string; fileName: string }> => {
    const fd = new FormData();
    fd.append("file", file);
    return apiFetch<{ blobKey: string; fileName: string }>("/api/email/attachments", { method: "POST", body: fd });
  },

  inbox: (params: { filter?: string; q?: string; page?: number; pageSize?: number }) => {
    const qs = new URLSearchParams();
    if (params.filter) qs.set("filter", params.filter);
    if (params.q) qs.set("q", params.q);
    qs.set("page", String(params.page ?? 1));
    qs.set("pageSize", String(params.pageSize ?? 50));
    return get<InboxPage>(`/api/email/inbox?${qs.toString()}`);
  },

  message: (id: number) => get<InboxMessageDetail>(`/api/email/messages/${id}`),

  markRead: (id: number) => post(`/api/email/messages/${id}/read`, {}),

  markUnread: (id: number) => post(`/api/email/messages/${id}/unread`, {}),

  markSpam: (id: number, blockScope: "sender" | "domain" | "none") =>
    post(`/api/email/messages/${id}/mark-spam`, { blockScope }),

  notSpam: (id: number) => post(`/api/email/messages/${id}/not-spam`, {}),

  rethread: (id: number, entityType: string, entityId: number) =>
    post(`/api/email/messages/${id}/rethread`, { entityType, entityId }),

  deleteMessage: (id: number) => apiFetch<void>(`/api/email/messages/${id}`, { method: "DELETE" }),

  bulk: (ids: number[], action: "read" | "unread" | "mark-spam" | "delete") =>
    post(`/api/email/messages/bulk`, { ids, action }),

  fromAddresses: () => get<FromAddress[]>(`/api/email/from-addresses`),
};
